import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { deletedPreviewHref } from '@/hooks/use-deleted-preview';
import { describeAlsoAffected, describeCounts } from '@/lib/utils/soft-delete';
import { getRestorePreview, type SoftDeleteTarget } from '@/queries/soft-delete/soft-delete';

import { WorkPackageList } from './WorkPackageList';

type RestoreConfirmDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	target: SoftDeleteTarget;
	name: string;
	isRestoring?: boolean;
	onConfirm: () => void | Promise<void>;
};

/**
 * Restore confirmation fed by the restore preview (GH #331): restore is all-or-nothing, so the
 * dialog says what comes back with the item, what stays deleted because another parent is still
 * deleted, and which work-package lines stay deleted. A work package whose line points to a damage
 * deleted on its own cannot be restored: the dialog links to that damage to restore it first.
 */
export function RestoreConfirmDialog({
	open,
	onOpenChange,
	target,
	name,
	isRestoring = false,
	onConfirm,
}: RestoreConfirmDialogProps) {
	const { t } = useTranslation();
	const preview = useQuery({
		queryKey: ['soft-delete', 'restore-preview', target.path],
		queryFn: () => getRestorePreview(target),
		enabled: open,
		gcTime: 0,
		staleTime: 0,
		meta: { skipGlobalErrorToast: true },
	});
	const data = preview.data;
	const alsoRestored = data ? describeAlsoAffected(data.willRestore, target.type) : '';
	const staysDeleted = data ? describeCounts(data.notRestored) : '';
	const blocked = (data?.blockedBy.length ?? 0) > 0;

	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('common.deleted.restoreTitle', { name })}</AlertDialogTitle>
					<AlertDialogDescription asChild>
						<div className="space-y-3 text-sm">
							{preview.isLoading && (
								<span className="flex items-center gap-2">
									<Spinner className="size-4" /> {t('common.deleted.previewLoading')}
								</span>
							)}
							{preview.isError && <p>{t('common.deleted.previewFailed')}</p>}
							{data && (
								<p>
									{alsoRestored
										? t('common.deleted.restoreAlso', { items: alsoRestored })
										: t('common.deleted.restoreNothingElse')}
								</p>
							)}
							{data?.blockedBy.map((blocker) => (
								<div key={blocker.code} className="space-y-1">
									<p className="flex items-start gap-2 font-medium text-destructive">
										<TriangleAlert className="mt-0.5 size-4 shrink-0" />
										{t(`common.deleted.restoreBlocked.${blocker.code}`, { count: blocker.damages.length })}
									</p>
									<ul className="max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5 text-sm">
										{blocker.damages.map((damage) => (
											<li key={damage.id}>
												<Link
													to={deletedPreviewHref(
														`/assets/${damage.assetId}/inspections/${damage.inspectionId}/damages/${damage.id}`,
														'damage',
													)}
													className="font-medium underline underline-offset-2 hover:text-primary"
												>
													{damage.title || t('common.deleted.damageUntitled')}
												</Link>
											</li>
										))}
									</ul>
								</div>
							))}
							{staysDeleted && <p>{t('common.deleted.restoreStaysDeleted', { items: staysDeleted })}</p>}
							{data && data.workPackagesNotRestored.length > 0 && (
								<div className="space-y-1">
									<p>{t('common.deleted.restoreNotRestored')}</p>
									<WorkPackageList workPackages={data.workPackagesNotRestored} />
								</div>
							)}
						</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={isRestoring}>{t('common.actions.cancel')}</AlertDialogCancel>
					<Button type="button" disabled={isRestoring || preview.isLoading || blocked} onClick={() => void onConfirm()}>
						{isRestoring && <Spinner className="size-4" />}
						{t('common.actions.restore')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
